import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { MortarboardFill, PeopleFill, Calendar2CheckFill, ChatDotsFill, ShieldCheck, Search } from 'react-bootstrap-icons';
import { NAVIGATION_HEIGHT } from '../constants/global';

const dividerColor = 'var(--colors-neutral6, #d1d5db)';

const Dividers = ({ opacity = 0.8 }: { opacity?: number }) => (
  <>
    <div className="landing-divider" style={{ position: 'absolute', top: 0, bottom: 0, left: '15%', width: '1px', background: dividerColor, opacity }} />
    <div className="landing-divider" style={{ position: 'absolute', top: 0, bottom: 0, right: '15%', width: '1px', background: dividerColor, opacity }} />
  </>
);

const LandingPage = () => {
  return (
    <>
      <Helmet>
        <title>tute — Oxford & Cambridge Tutors</title>
        <meta name="description" content="Connect your child with verified Oxford and Cambridge tutors. Book lessons, manage schedules, and track progress — all in one place." />
        <meta property="og:title" content="tute — Oxford & Cambridge Tutors" />
        <meta property="og:description" content="Connect your child with verified Oxford and Cambridge tutors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tute.academy" />
        <link rel="canonical" href="https://tute.academy" />
      </Helmet>

      <style>{`@media (max-width: 900px) { .landing-divider { display: none !important; } }`}</style>
      <div style={{ marginTop: NAVIGATION_HEIGHT }}>

        {/* Hero — grey background with skyline + dividers */}
        <section style={{
          background: '#f0f2f5', position: 'relative',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '3rem 1.5rem 3.5rem',
        }}>
          <AuthSkyline light />
          <Dividers />

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            background: '#dbeafe', color: '#1e40af', fontSize: '0.8rem', fontWeight: 600,
            padding: '0.35rem 0.9rem', borderRadius: '99px', marginBottom: '1.5rem',
            letterSpacing: '0.02em', position: 'relative', zIndex: 1,
          }}>
            <ShieldCheck size={14} />
            Verified Oxford & Cambridge tutors
          </div>

          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: 700, color: '#0f172a',
            lineHeight: 1.15, margin: '0 0 1rem', letterSpacing: '-0.03em',
            position: 'relative', zIndex: 1,
          }}>
            Exceptional tutoring from<br />
            <span style={{ color: '#2563eb' }}>Oxbridge students and graduates</span>
          </h1>

          <p style={{
            fontSize: 'clamp(0.95rem, 2.5vw, 1.15rem)', color: '#475569', lineHeight: 1.6,
            maxWidth: '550px', margin: '0 0 2rem', position: 'relative', zIndex: 1,
          }}>
            Connect your child with tutors from Oxford and Cambridge.
            Every tutor is verified, every lesson is tracked, and progress is visible from day one.
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
            <Link to="/register/parent" style={{
              padding: '0.75rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none',
              color: 'white', fontWeight: 600, fontSize: '0.95rem', background: '#2563eb',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
            }}>Find a tutor</Link>
            <Link to="/register/tutor" style={{
              padding: '0.75rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none',
              color: '#1e293b', fontWeight: 600, fontSize: '0.95rem', background: 'white',
              border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}>Apply as a tutor</Link>
          </div>
        </section>

        {/* Features — white background, no drawings */}
        <section style={{
          background: 'white',
          padding: '4rem 2rem 5rem',
        }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              textAlign: 'center', fontSize: '1.6rem', fontWeight: 700, color: '#0f172a',
              margin: '0 0 0.5rem',
            }}>How tute works</h2>
            <p style={{
              textAlign: 'center', color: '#64748b', fontSize: '1rem',
              margin: '0 0 3rem', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto',
            }}>
              A complete platform for parents and tutors
            </p>

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.25rem',
            }}>
              <FeatureCard
                icon={<Search size={22} />}
                title="Find the right tutor"
                description="Browse verified tutors from Oxford and Cambridge. Filter by subject, level, and availability to find the perfect match."
              />
              <FeatureCard
                icon={<Calendar2CheckFill size={22} />}
                title="Book lessons instantly"
                description="Choose a time that works, book in a few clicks, and it syncs straight to Google Calendar. No back-and-forth needed."
              />
              <FeatureCard
                icon={<ChatDotsFill size={22} />}
                title="Message directly"
                description="Chat with your tutor before and after lessons. Discuss goals, share resources, and stay aligned on progress."
              />
              <FeatureCard
                icon={<ShieldCheck size={22} />}
                title="Verified credentials"
                description="Every tutor's university, college, and qualifications are checked before they can appear on the platform."
              />
              <FeatureCard
                icon={<MortarboardFill size={22} />}
                title="Track every lesson"
                description="See upcoming and completed lessons at a glance. Both parents and tutors get a clear view of the schedule."
              />
              <FeatureCard
                icon={<PeopleFill size={22} />}
                title="Built for both sides"
                description="Whether you're a parent looking for support or a tutor building your practice, tute is designed for you."
              />
            </div>
          </div>
        </section>

        {/* CTA — blue with dividers */}
        <section style={{
          background: 'var(--colors-blue3, #dbeafe)', position: 'relative',
          padding: '3.5rem 2rem', textAlign: 'center',
        }}>
          <Dividers opacity={0.5} />
          <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', margin: '0 0 0.5rem', position: 'relative', zIndex: 1 }}>
            Ready to get started?
          </h2>
          <p style={{ color: '#475569', fontSize: '1rem', margin: '0 0 1.75rem', position: 'relative', zIndex: 1 }}>
            Join tute today — it takes less than two minutes.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
            <Link to="/register/parent" style={{
              padding: '0.75rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none',
              color: 'white', fontWeight: 600, fontSize: '0.95rem', background: '#2563eb',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
            }}>Find a tutor</Link>
            <Link to="/register/tutor" style={{
              padding: '0.75rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none',
              color: '#1e293b', fontWeight: 600, fontSize: '0.95rem', background: 'white',
              border: '1px solid #e2e8f0',
            }}>Apply as a tutor</Link>
          </div>
        </section>

        <footer style={{
          background: 'var(--colors-blue3, #dbeafe)',
          borderTop: '1px solid var(--colors-neutral6, #d1d5db)',
          padding: '1.25rem 2rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '0.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#0f172a' }}>tute</span>
            <span style={{ color: '#64748b', fontSize: '0.8rem' }}>© 2026</span>
          </div>
          <div style={{ display: 'flex', gap: '1.25rem' }}>
            <Link to="/login" style={{ color: '#475569', textDecoration: 'none', fontSize: '0.8rem' }}>Log in</Link>
            <Link to="/register" style={{ color: '#475569', textDecoration: 'none', fontSize: '0.8rem' }}>Sign up</Link>
            <Link to="/privacy" style={{ color: '#475569', textDecoration: 'none', fontSize: '0.8rem' }}>Privacy</Link>
            <Link to="/terms" style={{ color: '#475569', textDecoration: 'none', fontSize: '0.8rem' }}>Terms</Link>
          </div>
        </footer>
      </div>
    </>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div style={{
    padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0',
    background: 'white',
  }}>
    <div style={{
      width: '44px', height: '44px', borderRadius: '10px', background: '#dbeafe',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#2563eb', marginBottom: '0.75rem',
    }}>
      {icon}
    </div>
    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: '0 0 0.4rem' }}>{title}</h3>
    <p style={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{description}</p>
  </div>
);

export default LandingPage;
